import {
  encodeEntityReferenceToken,
  type EntityMentionType,
  type MentionableRecordDto,
} from '@paladin-monorepo/common';

/** The active `@` run in the composer. An empty `query` means the prefix stage. */
export interface MentionTrigger {
  /** Index of the `@` itself. */
  atIndex: number;
  /** Text typed after the `@`, up to the caret. */
  query: string;
}

/** A reference inserted into the draft, tracked until the message is sent. */
export interface TrackedReference {
  type: EntityMentionType;
  id: string;
  /** Bare record name, as carried by the token. */
  name: string;
  /** Translated type prefix, kept so the reference can collapse back to `@Prefix: `. */
  prefix: string;
  /** Exact text occupying the range, including the leading `@`. */
  text: string;
  start: number;
  end: number;
}

/** The `@Prefix: ` a reference collapses to when part of its name is deleted. */
export function referencePrefixText(reference: TrackedReference): string {
  return `@${reference.prefix}: `;
}

export type MentionPrefixes = Record<EntityMentionType, string>;

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Beyond this the run is almost certainly ordinary prose, not a mention being typed. */
const MAX_TRIGGER_RUN = 80;

/**
 * Finds the `@` run the caret sits in.
 *
 * `@` counts only at the start of the text or directly after whitespace, which is what keeps
 * an email address such as `pavel@codebros.cz` from opening the picker.
 *
 * Whitespace closes the run *until* a prefix has been completed. A completed prefix always
 * contains `:`, so once one is present the run may hold spaces - which is what lets
 * `@Decision case:` be typed at all, and what keeps the picker open after a prefix row is
 * picked (it completes to `@Offer: `, trailing space included).
 *
 * A `@` inside an already inserted reference is not a trigger; pass the tracked ranges so
 * `@Inquiry: Website redesign` does not re-open the picker over itself.
 */
export function findMentionTrigger(
  value: string,
  caret: number,
  references: TrackedReference[] = [],
): MentionTrigger | null {
  if (caret < 1 || caret > value.length) return null;

  const atIndex = value.lastIndexOf('@', caret - 1);
  if (atIndex === -1) return null;

  // Inside an existing reference: that `@` belongs to the inserted text.
  if (references.some((reference) => atIndex >= reference.start && atIndex < reference.end)) {
    return null;
  }

  const before = atIndex === 0 ? '' : value[atIndex - 1];
  if (before !== '' && !/\s/.test(before)) return null;

  const query = value.slice(atIndex + 1, caret);
  if (query.length > MAX_TRIGGER_RUN) return null;
  if (/[\r\n]/.test(query)) return null;
  // Spaces are allowed only once a prefix has been completed.
  if (/\s/.test(query) && !query.includes(':')) return null;

  return { atIndex, query };
}

/**
 * True when the picker should ask the server for records.
 *
 * Records wait for a completed type prefix; before that the picker offers prefixes only.
 * Once the prefix is in, suggestions appear with no name characters typed.
 */
export function shouldFetchRecords(parsed: ParsedMentionQuery): boolean {
  return parsed.type !== undefined;
}

/** Display text for a record: `Type: name`, with the translated prefix. */
export function formatMentionLabel(
  record: Pick<MentionableRecordDto, 'type' | 'name'>,
  prefixes: MentionPrefixes,
  fallbackName: string,
): string {
  return `${prefixes[record.type]}: ${record.name.trim() || fallbackName}`;
}

export interface ParsedMentionQuery {
  /** Set when the query names a type followed by `:`, e.g. `Offer:` or `Offer: redesign`. */
  type?: EntityMentionType;
  /** What remains to match against record names. */
  nameQuery: string;
  /** Types whose prefix starts with the query, for the rows the picker offers. */
  typeMatches: EntityMentionType[];
}

/**
 * Splits the typed query into a type filter and a name query.
 *
 * The translated prefix is matched here rather than on the server, so the backend never needs
 * translations and a typed prefix still acts as a type filter.
 */
export function parseMentionQuery(
  query: string,
  prefixes: MentionPrefixes,
  availableTypes: EntityMentionType[],
): ParsedMentionQuery {
  const normalizedQuery = normalize(query.trim());

  for (const type of availableTypes) {
    const prefix = normalize(prefixes[type]);
    if (!normalizedQuery.startsWith(`${prefix}:`)) continue;
    return {
      type,
      nameQuery: query.trim().slice(prefixes[type].length + 1).trim(),
      typeMatches: [type],
    };
  }

  const typeMatches = normalizedQuery
    ? availableTypes.filter((type) => normalize(prefixes[type]).startsWith(normalizedQuery))
    : availableTypes;

  return { nameQuery: query.trim(), typeMatches };
}

export interface InsertionResult {
  value: string;
  /** Where the caret belongs afterwards: directly after the inserted reference. */
  caret: number;
  reference: TrackedReference;
}

/** Replaces the `@` run with `@Type: name`, recording the range it occupies. */
export function insertMention(
  value: string,
  trigger: MentionTrigger,
  record: MentionableRecordDto,
  prefixes: MentionPrefixes,
  fallbackName: string,
): InsertionResult {
  const label = formatMentionLabel(record, prefixes, fallbackName);
  const text = `@${label}`;
  const runEnd = trigger.atIndex + 1 + trigger.query.length;
  const nextValue = value.slice(0, trigger.atIndex) + text + value.slice(runEnd);

  return {
    value: nextValue,
    caret: trigger.atIndex + text.length,
    reference: {
      type: record.type,
      id: record.id,
      name: record.name.trim() || fallbackName,
      prefix: prefixes[record.type],
      text,
      start: trigger.atIndex,
      end: trigger.atIndex + text.length,
    },
  };
}

/** Completes the `@` run to `@Type: `, so the next keystrokes filter that type only. */
export function completeMentionPrefix(
  value: string,
  trigger: MentionTrigger,
  type: EntityMentionType,
  prefixes: MentionPrefixes,
): { value: string; caret: number } {
  const text = `@${prefixes[type]}: `;
  const runEnd = trigger.atIndex + 1 + trigger.query.length;
  return {
    value: value.slice(0, trigger.atIndex) + text + value.slice(runEnd),
    caret: trigger.atIndex + text.length,
  };
}

/**
 * Re-anchors tracked ranges against an edited draft.
 *
 * A range whose exact text no longer sits at its offsets is searched for once; if the text is
 * gone entirely the reference is dropped, which is how editing a reference by hand removes it.
 */
export function realignReferences(
  value: string,
  references: TrackedReference[],
): TrackedReference[] {
  const aligned: TrackedReference[] = [];
  let searchFrom = 0;

  for (const reference of [...references].sort((a, b) => a.start - b.start)) {
    if (value.slice(reference.start, reference.end) === reference.text) {
      aligned.push(reference);
      searchFrom = Math.max(searchFrom, reference.end);
      continue;
    }

    const found = value.indexOf(reference.text, searchFrom);
    if (found === -1) continue;

    aligned.push({ ...reference, start: found, end: found + reference.text.length });
    searchFrom = found + reference.text.length;
  }

  return aligned;
}

/**
 * The reference a deletion keystroke would eat into.
 *
 * A reference is one unit, so deleting any part of its name must take the whole name rather
 * than leave a mangled caption. Backspace looks behind the caret, Delete looks ahead, and a
 * selection catches any reference it overlaps.
 */
export function findReferenceToCollapse(
  references: TrackedReference[],
  selectionStart: number,
  selectionEnd: number,
  direction: 'backward' | 'forward',
): TrackedReference | null {
  if (selectionStart !== selectionEnd) {
    return (
      references.find(
        (reference) => selectionStart < reference.end && selectionEnd > reference.start,
      ) ?? null
    );
  }

  const caret = selectionStart;
  if (direction === 'backward') {
    // Caret at `start` would delete the character before the reference, not part of it.
    return references.find((reference) => caret > reference.start && caret <= reference.end) ?? null;
  }
  return references.find((reference) => caret >= reference.start && caret < reference.end) ?? null;
}

/**
 * Replaces a reference with its bare `@Prefix: `, leaving the caret after it.
 *
 * That is the state the picker treats as a completed prefix, so the user is immediately
 * offered the same type's records and can pick a different one without retyping anything.
 */
export function collapseReferenceToPrefix(
  value: string,
  references: TrackedReference[],
  target: TrackedReference,
): { value: string; caret: number; references: TrackedReference[] } {
  const prefixText = referencePrefixText(target);
  const nextValue = value.slice(0, target.start) + prefixText + value.slice(target.end);

  return {
    value: nextValue,
    caret: target.start + prefixText.length,
    references: realignReferences(
      nextValue,
      references.filter((reference) => reference !== target),
    ),
  };
}

/**
 * Turns the readable draft into the content that gets sent: every tracked range becomes a
 * `[[ref:type:id|name]]` token. Untracked `@` text is left exactly as typed.
 */
export function toContentWithTokens(value: string, references: TrackedReference[]): string {
  const aligned = realignReferences(value, references).sort((a, b) => a.start - b.start);
  if (aligned.length === 0) return value;

  let result = '';
  let cursor = 0;
  for (const reference of aligned) {
    if (reference.start < cursor) continue;
    result += value.slice(cursor, reference.start);
    result += encodeEntityReferenceToken(reference.type, reference.id, reference.name);
    cursor = reference.end;
  }
  return result + value.slice(cursor);
}
